import React, { FC } from 'react';
import styled from '@emotion/styled';
import { Flex } from '$components/layouts';
import { Text } from '$components/elements/text';
import { useTranslation } from '$hooks';
import { isDefined, mq } from '$lib/helpers';
import { IEnrichedProduct, IEnrichedProductPerson, IReview } from '$models';
import { Description, Details, Reviews } from '../product-info-side-panel/side-panel-content';
import { Collapsible } from '$components/elements/collapsible';
import { PersonRoles } from '$constants';
import { ProductThemas } from '../product-themas';

type ProductAboutLinksProps = {
    isBook?: boolean;
    product: IEnrichedProduct;
    reviews: IReview;
    onOpenReviewModal: () => void;
    persons?: IEnrichedProductPerson[];
};

export const ProductAboutLinks: FC<ProductAboutLinksProps> = ({
    isBook,
    reviews,
    product,
    onOpenReviewModal,
    persons,
}: ProductAboutLinksProps): JSX.Element => {
    const { translate } = useTranslation();
    const authors =
        persons?.filter((person: IEnrichedProductPerson) =>
            person?.personTypes?.some((type) => type === PersonRoles.Author)
        ) || [];

    return (
        <div>
            <Container>
                <Title h2 bottomMargin="6" font="regular">
                    {`${
                        isBook
                            ? translate('productPage.bookDescription.publisherDescription')
                            : translate('productPage.productDescription.productDescription')
                    } `}
                    <Text variant="bodyLarge" lineHeight="30px" style={{ display: 'block' }}>
                        {isBook && authors.length
                            ? `${product?.name} ${translate('productPage.productDetails.of')} ${
                                  authors.find((a) => isDefined(a.personName))?.personName
                              }`
                            : product?.name}
                    </Text>
                </Title>
                <Content column>
                    <Collapsible
                        chevron
                        collapsedHeight={120}
                        collapseCTAText={translate('productPage.productDescription.showLessDesciption')}
                        expandCTAText={translate('productPage.productDescription.showEntireDesciption')}
                    >
                        <Flex column>
                            <Description product={product} />
                        </Flex>
                    </Collapsible>
                </Content>
            </Container>
            <Container>
                <Title h2 bottomMargin="6" font="regular">
                    {isBook
                        ? translate('productPage.bookDescription.details')
                        : translate('productPage.productDescription.details')}
                </Title>
                <Content column>
                    <Collapsible
                        chevron
                        collapsedHeight={180}
                        collapseCTAText={translate('productPage.productDescription.showLessDetails')}
                        expandCTAText={translate('productPage.productDescription.showAllDetails')}
                    >
                        <Details product={product} />
                    </Collapsible>
                </Content>
            </Container>
            {!!product.themaCodeBreadCrumbs?.length && (
                <Container>
                    <Title h2 bottomMargin="6" font="regular">
                        {`${translate('productPage.productThemas.title')} `}
                        <Text variant="bodyLarge" lineHeight="30px" style={{ display: 'block' }}>
                            {translate('productPage.productThemas.description')}
                        </Text>
                    </Title>

                    <Content column>
                        <Collapsible
                            chevron
                            collapseCTAText={translate('productPage.productDescription.showLessDetails')}
                            expandCTAText={translate('productPage.productDescription.showAllDetails')}
                        >
                            <ProductThemas themaCodes={product.themaCodeBreadCrumbs} />
                        </Collapsible>
                    </Content>
                </Container>
            )}
            <Container>
                <Title h2 bottomMargin="6" font="regular">
                    {`${translate('productPage.linkHeaders.reviews')} `}
                    <Text variant="bodyLarge" lineHeight="30px" style={{ display: 'block' }}>
                        {product?.name}
                    </Text>
                </Title>
                <Content>
                    <Reviews product={product.product} reviews={reviews} onOpenReviewModal={onOpenReviewModal} />
                </Content>
            </Container>
        </div>
    );
};

const Container = styled(Flex)(({ theme }) => ({
    fontSize: theme.fontSizes.h2,
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginBottom: theme.space[6],
    paddingBottom: theme.space[6],
    paddingTop: theme.space[6],
    borderBottom: `1px solid ${theme.colors.borderColor}`,
    '&:last-child': {
        borderBottom: 0,
    },
    [mq('frame')]: {
        gridTemplateColumns: '1fr 1fr',
    },
}));

const Title = styled(Text)(({ theme }) => ({
    fontSize: theme.fontSizes.h5,
    [mq('frame')]: {
        fontSize: theme.fontSizes.h3,
    },
    [mq('lg')]: {
        fontSize: theme.fontSizes.h1,
    },
}));

const Content = styled(Flex)({
    [mq('frame')]: {
        paddingLeft: '24px',
        paddingRight: '24px',
    },
});
